import React, { useState, useEffect, useContext } from "react";
import "../../../assets/css/customer/shop/subscreens/pd_ProductGallery.css";
import AppContext from "../../../context/AppContext";
import { ImageOff } from "lucide-react";

const ProductGallery = ({ images, productName, onZoomChange }) => {
  const { baseUrlForFile } = useContext(AppContext);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  // Fallback for missing images
  const productImages = Array.isArray(images)
    ? images
    : images
      ? [images]
      : [
          {
            image_path:
              "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg",
          },
        ];
  // <ImageOff size={20} className="cat-no-image" />

  // Handle zoom toggle
  const toggleZoom = () => {
    const newZoomState = !isZoomed;
    setIsZoomed(newZoomState);
    if (onZoomChange) {
      onZoomChange(newZoomState);
    }
  };

  // Close zoom on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isZoomed) {
        toggleZoom();
      }
    };

    if (isZoomed) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isZoomed]);

  return (
    <>
      {/* Zoomed Fullscreen View */}
      {isZoomed && (
        <div className="pd-zoomed-overlay">
          <div
            className="pd-main-image-container pd-zoomed"
            onClick={toggleZoom}
          >
            <img
              src={`${baseUrlForFile}${productImages[selectedImage]?.image_path}`}
              alt={`${productName} - Zoomed View`}
              className="pd-main-image"
            />

            {/* Close Button */}
            <button
              className="pd-zoom-close"
              onClick={(e) => {
                e.stopPropagation();
                toggleZoom();
              }}
              aria-label="Close zoom"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Normal Gallery View */}
      <div
        className="pd-gallery"
        style={{ display: isZoomed ? "none" : "block" }}
      >
        {/* Main Image */}
        <div className="pd-main-image-wrapper">
          <div
            className={`pd-main-image-container ${isZoomed ? "pd-zoomed" : ""}`}
            onClick={toggleZoom}
          >
            <img
              src={`${baseUrlForFile}${productImages[selectedImage]?.image_path}`}
              alt={`${productName} - View ${selectedImage + 1}`}
              className="pd-main-image"
            />

            {/* Zoom Icon */}
            {!isZoomed && (
              <div className="pd-zoom-hint">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                  />
                </svg>
                <span>Click to zoom</span>
              </div>
            )}

            {/* Close Button (when zoomed) */}
            {isZoomed && (
              <button
                className="pd-zoom-close"
                onClick={toggleZoom}
                aria-label="Close zoom"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Image Counter */}
          {!isZoomed && (
            <div className="pd-image-counter">
              {selectedImage + 1} / {productImages.length}
            </div>
          )}
        </div>

        {/* Thumbnail Gallery */}
        {productImages.length > 1 && (
          <div className="pd-thumbnail-gallery">
            {productImages.map((image, index) => (
              <button
                key={index}
                className={`pd-thumbnail ${selectedImage === index ? "pd-thumbnail-active" : ""}`}
                onClick={() => setSelectedImage(index)}
              >
                <img
                  src={`${baseUrlForFile}${image.image_path}`}
                  alt={`Thumbnail ${index + 1}`}
                />
              </button>
            ))}
          </div>
        )}

        {/* Navigation Arrows */}
        {productImages.length > 1 && (
          <>
            <button
              className="pd-nav-arrow pd-nav-prev"
              onClick={() =>
                setSelectedImage((prev) =>
                  prev === 0 ? productImages.length - 1 : prev - 1,
                )
              }
              aria-label="Previous image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              className="pd-nav-arrow pd-nav-next"
              onClick={() =>
                setSelectedImage((prev) =>
                  prev === productImages.length - 1 ? 0 : prev + 1,
                )
              }
              aria-label="Next image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default ProductGallery;
